/* eslint-disable no-nested-ternary */
/* eslint-disable eqeqeq */
import React, { useContext, useState, useMemo, useEffect, Fragment } from 'react';
import { DropDownList } from 'components/formInputs/dropDownList';
import { useNavigate } from 'react-router-dom';
import { Context as AuthContext } from 'contexts/auth';
import { Context as CompanyContext } from 'contexts/company';
import { Context as AlertContext } from 'contexts/alert';
import { Config } from 'configs';
import { useFind, useAggregate } from 'hooks';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { enrichProductCategoriesResponse, sortByRootParent } from 'utils/renderingHelpers';
import {
  fillTranslatableTexts,
  getValidationPairsForTranslatableText,
  getValidationShapeForTranslatableText,
  TranslationFieldOptions,
} from 'utils/schemaHelper';
import { TranslatableInput } from 'components/formInputs/tranlatableInput';
import { DataColumn, LinkColumn, List } from 'components/lists';
import { ImageUpload } from 'components/formInputs/imageUpload';
import { files } from 'codemash';
import { formFileUrlFromFileArray } from 'utils/imageHelper';
import { Input } from 'components/formInputs/input';
import { joinClassNames } from 'utils/cssHelper';
import { SelectedItemButton } from 'components/buttons/selectedItemButton';
import { getTranslatedData } from 'locales';
import { FormButton } from 'components/buttons/formButton';
import { CheckboxWrapper } from 'components/formInputs/wrappers/checkboxWrapper';
import { TextboxWrapper } from 'components/formInputs/wrappers/textboxWrapper';
import { DropdownWrapper } from 'components/formInputs/wrappers/dropdownWrapper';
import { sortObjectArray } from 'utils/arrayHelpers';
import { CheckCircleIcon } from '@heroicons/react/solid';
import { apiActionChooser } from 'utils/apiActionChooser';
import { postFile } from 'api/client/requests';
import { ProductVariationTemplatesAddEditForm } from './_popup_product_variations';
import { ProductCustomTagAddEditForm } from './_popup_custom_tag';

const displayTypeOption = [
  { value: 'any', label: 'Dine-in and Delivery' },
  { value: 'dinein', label: 'Only Dine-in' },
  { value: 'delivery', label: 'Only Delivery' },
];

export const ProductsAddNewMainInfo = ({
  itemData,
  isEdit,
  restaurants,
  onRestaurantsChange,
  onProductVariationsChange,
}) => {
  const navigate = useNavigate();
  const urlRootName = '/products';
  const isNew = !isEdit;
  const { addNew, editById, deleteById, getById } = useContext(CompanyContext);
  const { alertController } = useContext(AlertContext);
  const { state: auth } = useContext(AuthContext);
  const [selectedVariation, setSelectedVariation] = useState(null);
  const [modalIsVisible, setModalIsVisible] = useState(false);
  const [selectedRestaurants, setSelectedRestaurants] = useState(itemData?.restaurants || []);
  const [hiddenRestaurants, setHiddenRestaurants] = useState(itemData?.hidden_in_restaurants || []);
  const [selectedProductCategories, setSelectedProductCategories] = useState(
    itemData?.categories || [],
  );
  const [selectedProductCategoriesOverride, setSelectedProductCategoriesOverride] = useState(
    itemData?.categories_override || [],
  );
  const [selectedVariationJoinsOverride, setSelectedVariationJoinsOverride] = useState([]);
  const [companyData, setCompanyData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [loadingVariationJoins, setLoadingVariationJoins] = useState(false);
  const [isSynced, setIsSynced] = useState(!!itemData?.rkeeper_dish_code);
  const [customTags, setCustomTags] = useState(itemData?.tags);
  const [selectedTag, setSelectedTag] = useState(null);
  const [selectedTagIndex, setSelectedTagIndex] = useState(-1);
  const [tagModalIsVisible, setTagModalIsVisible] = useState(false);

  const [isLongDeliver, setIsLongDelivery] = useState(false);

  const overridableProductsRequestData = {
    collectionName: Config.DATA.COLLECTIONS.WEB_USERS,
    aggregateId: Config.DATA.AGGREGATES.GET_ALL_COMPANY_PRODUCTS,
    secretKey: auth.token,
    filter: { company: auth.company?.company },
    aggregateIdV2: 'AdminGetCompanyProducts',
    companyIdForV2: auth.company?.company,
  };

  const [, allProducts] = useAggregate(overridableProductsRequestData);

  useEffect(() => {
    const findCompanyById = async () => {
      const getItemResponse = await getById({
        collectionName: Config.DATA.COLLECTIONS.COMPANY,
        token: auth.token,
        id: auth.company.company,
        resourceV2: 'AdminCompany',
        companyIdForV2: auth.company?.company,
      });

      if (getItemResponse) {
        setCompanyData(getItemResponse);
      }
    };

    if (isNew && auth.company.company) {
      findCompanyById();
    }
  }, []);

  const [coverImages, setCoverImages] = useState([]);
  const [listItemImages, setListItemImages] = useState([]);

  const getVariationsRequestData = {
    secretKey: auth.token,
    collectionName: Config.DATA.COLLECTIONS.PRODUCT_VARIATIONS,
    filter: `{ product: ObjectId('${itemData?._id}') }`,
    excludeCulture: true,
    noFindOnLoad: !isEdit,
    resourceV2: 'AdminProductVariation',
    companyIdForV2: auth.company?.company,
  };

  const [getProductVariations, productVariations] = useFind(getVariationsRequestData);

  const getVariationsJoinsRequestData = {
    secretKey: auth.token,
    collectionName: Config.DATA.COLLECTIONS.VARIATION_JOINS,
    filter: `{ company: ObjectId('${auth.company.company}') }`,
    excludeCulture: true,
    noFindOnLoad: !isEdit,
    resourceV2: 'AdminVariationJoin',
    companyIdForV2: auth.company?.company,
  };

  const [, variationJoins] = useFind(getVariationsJoinsRequestData);

  const getProductVariationsJoinRequestData = {
    secretKey: auth.token,
    collectionName: Config.DATA.COLLECTIONS.PRODUCT_VARIATION_JOINS,
    filter: `{ company: ObjectId('${auth.company.company}'), product: ObjectId('${itemData?._id}') }`,
    excludeCulture: true,
    noFindOnLoad: !isEdit,
    pageSize: 1,
    pageNumber: 0,
    resourceV2: 'AdminProductVariationJoin',
    companyIdForV2: auth.company?.company,
  };

  const [getProductVariationJoin, productVariationJoin] = useFind(
    getProductVariationsJoinRequestData,
  );

  useEffect(() => {
    if (productVariations) {
      sortObjectArray(productVariations, 'order', 1, true);
      onProductVariationsChange(productVariations);
    }
  }, [productVariations]);

  useEffect(() => {
    if (productVariationJoin && productVariationJoin.length > 0) {
      setSelectedVariationJoinsOverride(productVariationJoin[0].joins || []);
    }
  }, [productVariationJoin]);

  const getProductCategoriesRequestData = {
    collectionName: Config.DATA.COLLECTIONS.PRODUCT_CATEGORIES,
    aggregateId: Config.DATA.AGGREGATES.PRODUCT_CATEGORIES_V2,
    secretKey: auth.token,
    filter: { company: auth.company?.company, appLanguage: 'en' },
    pageSize: 100,
    pageNumber: 0,
    aggregateIdV2: 'AdminGetProductCategories',
    companyIdForV2: auth.company?.company,
  };

  const [, productCategories] = useAggregate(getProductCategoriesRequestData);

  const schema = yup.object().shape(
    {
      _id: yup.string().default(itemData?._id),
      restaurants: yup
        .array()
        .of(yup.string())
        .required('Please enter a restaurants.')
        .min(1, 'Please select at least one restaurant.'),
      categories: yup.array().of(yup.string()),
      categories_override: yup.array().of(yup.string()),
      served_from_bar: yup.boolean().default(itemData?.served_from_bar || false),
      /*
        .when('served_from_kitchen', {
          is: (servedFromKitchen) => !servedFromKitchen,
          then: yup.boolean().oneOf([true], 'At least one of the fields is required'),
          otherwise: yup.boolean(),
        }), */

      served_from_kitchen: yup.boolean().default(itemData?.served_from_kitchen || false),
      /*
        .when('served_from_bar', {
          is: (servedFromBar) => !servedFromBar,
          then: yup.boolean().oneOf([true], 'At least one of the fields is required'),
          otherwise: yup.boolean(),
        }), */
      is_new: yup.boolean().default(itemData?.is_new || false),
      is_popular: yup.boolean().default(itemData?.is_popular || false),
      is_vegetarian: yup.boolean().default(itemData?.is_vegetarian || false),
      is_age_restricted: yup.boolean().default(itemData?.is_age_restricted || false),
      is_recommended: yup.boolean().default(itemData?.is_recommended || false),
      spicy: yup
        .number()
        .nullable(true)
        .min(0, 'Must be greather than 0')
        .max(3, 'Max 3')
        .transform((_, val) => (val == Number(val) && val !== '' ? Number(val) : null))
        .default(itemData?.spicy || 0),
      order: yup
        .number()
        .nullable(true)
        .transform((_, val) => (val == Number(val) && val !== '' ? Number(val) : null))
        .default(itemData?.order || 0),
      name: yup
        .object()
        .shape(
          getValidationShapeForTranslatableText(
            itemData?.name,
            TranslationFieldOptions.REQUIRE_AT_LEAST_ONE,
          ),
          getValidationPairsForTranslatableText(),
        ),
      prepare_from: yup
        .number()
        .typeError('Time should be interger which represents minutes')
        .nullable(true)
        .transform((_, val) => (val === '' ? null : val == Number(val) ? Number(val) : val))
        .default(itemData?.prepare_from),
      prepare_to: yup
        .number()
        .typeError('Time should be interger which represents minutes')
        .transform((_, val) => (val === '' ? null : val == Number(val) ? Number(val) : val))
        .nullable(true)
        .default(itemData?.prepare_to),
      is_long_delivery: yup.boolean().default(itemData?.is_long_delivery || false),
      long_delivery_prepare: yup
        .number()
        .typeError('Should be interger which represents days')
        .transform((_, val) => (val === '' ? null : val == Number(val) ? Number(val) : val))
        .nullable(true)
        .default(itemData?.long_delivery_prepare),
      description: yup
        .object()
        .shape(
          getValidationShapeForTranslatableText(
            itemData?.description,
            TranslationFieldOptions.OPTIONAL,
          ),
        ),
      nutrition: yup
        .object()
        .shape(
          getValidationShapeForTranslatableText(
            itemData?.nutrition,
            TranslationFieldOptions.OPTIONAL,
          ),
        ),
      hidden: yup.boolean().default(itemData?.hidden || false),
      display_only_as_variation: yup
        .boolean()
        .default(itemData?.display_only_as_variation || false),
      // is_overridable: yup.boolean().default(itemData?.is_overridable || false),
      manual_rkeeper_dish_code: yup.string().default(itemData?.manual_rkeeper_dish_code),
      display_option: yup.string().default(itemData?.display_option || 'any'),
    },
    [['served_from_bar', 'served_from_kitchen']],
  );

  const {
    register,
    reset,
    handleSubmit,
    setValue,
    formState: { errors },
  } = useForm({
    defaultValues: useMemo(() => {
      return schema.default();
    }, [schema]),
    resolver: yupResolver(schema),
  });

  useEffect(() => {
    setSelectedRestaurants(itemData?.restaurants || []);
    setHiddenRestaurants(itemData?.hidden_in_restaurants || []);
    setSelectedProductCategories(itemData?.categories || []);
    setSelectedProductCategoriesOverride(itemData?.categories_override || []);

    // setSelectedPublishingOption(publishingOptions.find((option) => option.id === publishingState));
    setIsSynced(!!itemData?.rkeeper_dish_code);
    setIsLongDelivery(itemData?.is_long_delivery || false);

    if (itemData?._id) {
      getProductVariations(getVariationsRequestData, true);

      if (itemData?.cover_image) {
        setCoverImages([{ data_url: formFileUrlFromFileArray(itemData?.cover_image, true) }]);
      }

      if (itemData?.list_image) {
        setListItemImages([{ data_url: formFileUrlFromFileArray(itemData?.list_image, true) }]);
      }
    }

    reset(itemData);
  }, [itemData, reset]);

  const saveProductVariationJoins = async () => {
    setLoadingVariationJoins(true);

    if (productVariationJoin && productVariationJoin.length > 0) {
      await editById(
        {
          collectionName: Config.DATA.COLLECTIONS.PRODUCT_VARIATION_JOINS,
          id: productVariationJoin[0]._id,
          updateClause: { $set: { joins: (selectedVariationJoinsOverride || []).map((x) => ({ $oid: x })) } },
          token: auth.token,
          resourceV2: 'AdminProductVariationJoin',
          updateTypeV2: 'General',
          updateDataV2: { joins: selectedVariationJoinsOverride || [] },
          companyIdForV2: auth.company?.company,
        },
        async () => {
          alertController.success({ text: 'Product variation joins saved' });
          setLoadingVariationJoins(false);
        },
        () => {
          setLoadingVariationJoins(false);
        },
      );
    } else {
      await addNew(
        {
          collectionName: Config.DATA.COLLECTIONS.PRODUCT_VARIATION_JOINS,
          data: {
            company: auth.company.company,
            product: itemData._id,
            joins: selectedVariationJoinsOverride || [],
          },
          token: auth.token,
          resourceV2: 'AdminProductVariationJoin',
          companyIdForV2: auth.company?.company,
        },
        async () => {
          alertController.success({ text: 'Product variation joins saved' });
          getProductVariationJoin(undefined, true);
          setLoadingVariationJoins(false);
        },
        () => {
          setLoadingVariationJoins(false);
        },
      );
    }
  };

  const onSubmit = async (data) => {
    setLoading(true);
    const submitedData = data;

    submitedData.restaurants = selectedRestaurants;
    submitedData.hidden_in_restaurants = hiddenRestaurants;
    submitedData.categories = selectedProductCategories;
    submitedData.categories_override = selectedProductCategoriesOverride;

    submitedData.name = fillTranslatableTexts(submitedData.name);

    const sumbitData = { ...submitedData, company: auth.company?.company };

    if (isNew) {
      if (companyData?.default_vat_size) {
        sumbitData.vat_size = companyData.default_vat_size;
      }

      await apiActionChooser(
        auth.company?.company,
        async () => {
          await addNew(
            {
              collectionName: Config.DATA.COLLECTIONS.PRODUCTS,
              data: sumbitData,
              token: auth.token,
            },
            async (newId) => {
              if (coverImages && coverImages.length > 0) {
                await files.uploadRecordFile({
                  collectionName: Config.DATA.COLLECTIONS.PRODUCTS,
                  database: Config.CODEMASH.DATABASE_ID,
                  recordId: newId,
                  uniqueFieldName: 'cover_image',
                  secretKey: auth.token,
                  file: coverImages[0].file,
                });
              }

              if (listItemImages && listItemImages.length > 0) {
                await files.uploadRecordFile({
                  collectionName: Config.DATA.COLLECTIONS.PRODUCTS,
                  database: Config.CODEMASH.DATABASE_ID,
                  recordId: newId,
                  uniqueFieldName: 'list_image',
                  secretKey: auth.token,
                  file: listItemImages[0].file,
                });
              }

              navigate(`${urlRootName}/${newId}?callback=true`);
            },
            () => {
              setLoading(false);
            },
          );
        },
        async () => {
          if (coverImages && coverImages.length > 0) {
            const fileResp = await postFile({
              fieldPath: 'cover_image',
              file: coverImages[0].file,
              resource: 'Products',
            });

            sumbitData.cover_image = fileResp.id;
          }

          if (listItemImages && listItemImages.length > 0) {
            const fileResp = await postFile({
              fieldPath: 'list_image',
              file: listItemImages[0].file,
              resource: 'Products',
            });

            sumbitData.list_image = fileResp.id;
          }

          await addNew(
            {
              data: sumbitData,
              resourceV2: 'AdminProduct',
              companyIdForV2: auth.company?.company,
            },
            async (newId) => {
              navigate(`${urlRootName}/${newId}?callback=true`);
            },
            () => {
              setLoading(false);
            },
          );
        },
      );
    } else {
      await apiActionChooser(
        auth.company?.company,
        async () => {
          await editById(
            {
              collectionName: Config.DATA.COLLECTIONS.PRODUCTS,
              id: sumbitData._id,
              updateClause: isSynced
                ? `{ $set: { "hidden": ${submitedData.hidden}, "display_only_as_variation": ${
                  submitedData.display_only_as_variation
                }, "manual_rkeeper_dish_code": '${submitedData.manual_rkeeper_dish_code}',
              "restaurants": ${JSON.stringify(
    submitedData.restaurants ? submitedData.restaurants.map((x) => ({ $oid: x })) : [],
  )}, 
              "hidden_in_restaurants": ${JSON.stringify(
    submitedData.hidden_in_restaurants
      ? submitedData.hidden_in_restaurants.map((x) => ({ $oid: x }))
      : [],
  )},
              "categories": ${JSON.stringify(
    submitedData.categories ? submitedData.categories.map((x) => ({ $oid: x })) : [],
  )},
              "categories_override": ${JSON.stringify(
    submitedData.categories_override
      ? submitedData.categories_override.map((x) => ({ $oid: x }))
      : [],
  )},
              "name": ${JSON.stringify(submitedData.name)}, "description": ${JSON.stringify(
  submitedData.description,
)}, "is_new" : ${submitedData.is_new}, "is_popular" : ${
  submitedData.is_popular
}, "is_vegetarian" : ${submitedData.is_vegetarian}, "is_age_restricted" : ${
  submitedData.is_age_restricted
}, "is_recommended" : ${submitedData.is_recommended}, "spicy" : ${
  submitedData.spicy
}, "is_long_delivery": ${
  submitedData.is_long_delivery || false
}, "long_delivery_prepare": ${submitedData.long_delivery_prepare || 0}, 
  "prepare_from": ${submitedData.prepare_from || 0}, "prepare_to": ${
  submitedData.prepare_to || 0
}, "order": ${submitedData.order || 0},  "nutrition": ${JSON.stringify(
  submitedData.nutrition,
)} }}`
                : `{ $set: { "hidden": ${submitedData.hidden}, "display_only_as_variation": ${
                  submitedData.display_only_as_variation
                }, "restaurants": ${JSON.stringify(
                  submitedData.restaurants
                    ? submitedData.restaurants.map((x) => ({ $oid: x }))
                    : [],
                )},
              "hidden_in_restaurants": ${JSON.stringify(
    submitedData.hidden_in_restaurants
      ? submitedData.hidden_in_restaurants.map((x) => ({ $oid: x }))
      : [],
  )},
              "categories": ${JSON.stringify(
    submitedData.categories ? submitedData.categories.map((x) => ({ $oid: x })) : [],
  )}, "manual_rkeeper_dish_code": '${submitedData.manual_rkeeper_dish_code}',
              "categories_override": ${JSON.stringify(
    submitedData.categories_override
      ? submitedData.categories_override.map((x) => ({ $oid: x }))
      : [],
  )},
               "name": ${JSON.stringify(submitedData.name)}, "description": ${JSON.stringify(
  submitedData.description,
)}, "display_option": '${submitedData.display_option || ''}',
               "is_new" : ${submitedData.is_new}, "is_popular" : ${
  submitedData.is_popular
}, "is_vegetarian" : ${submitedData.is_vegetarian}, "is_age_restricted" : ${
  submitedData.is_age_restricted
}, "is_recommended" : ${submitedData.is_recommended}, "spicy" : ${
  submitedData.spicy
}, "is_long_delivery": ${
  submitedData.is_long_delivery || false
}, "long_delivery_prepare": ${submitedData.long_delivery_prepare || 0},
   "prepare_from": ${submitedData.prepare_from || 0}, "prepare_to": ${
  submitedData.prepare_to || 0
}, "order": ${submitedData.order || 0}, "nutrition": ${JSON.stringify(
  submitedData.nutrition,
)} }}`,
              token: auth.token,
            },
            async () => {
              if (coverImages && coverImages.length > 0) {
                const uploadedNewImage =
                  coverImages[0].file !== null && coverImages[0].file !== undefined;

                if (uploadedNewImage) {
                  await files.uploadRecordFile({
                    collectionName: Config.DATA.COLLECTIONS.PRODUCTS,
                    database: Config.CODEMASH.DATABASE_ID,
                    recordId: sumbitData._id,
                    uniqueFieldName: 'cover_image',
                    secretKey: auth.token,
                    file: coverImages[0].file,
                  });
                }
              } else {
                await editById({
                  collectionName: Config.DATA.COLLECTIONS.PRODUCTS,
                  id: sumbitData._id,
                  updateClause: { $set: { cover_image: null } },
                  token: auth.token,
                });
              }

              if (listItemImages && listItemImages.length > 0) {
                const uploadedNewImage =
                  listItemImages[0].file !== null && listItemImages[0].file !== undefined;

                if (uploadedNewImage) {
                  files.uploadRecordFile({
                    collectionName: Config.DATA.COLLECTIONS.PRODUCTS,
                    database: Config.CODEMASH.DATABASE_ID,
                    recordId: sumbitData._id,
                    uniqueFieldName: 'list_image',
                    secretKey: auth.token,
                    file: listItemImages[0].file,
                  });
                }
              } else {
                await editById({
                  collectionName: Config.DATA.COLLECTIONS.PRODUCTS,
                  id: sumbitData._id,
                  updateClause: { $set: { list_image: null } },
                  token: auth.token,
                });
              }

              navigate(urlRootName);
            },
            () => {
              setLoading(false);
            },
          );
        },
        async () => {
          if (coverImages && coverImages.length > 0) {
            const uploadedNewImage =
              coverImages[0].file !== null && coverImages[0].file !== undefined;

            if (uploadedNewImage) {
              const fileResp = await postFile({
                fieldPath: 'cover_image',
                file: coverImages[0].file,
                resource: 'Products',
              });

              sumbitData.new_cover_image = fileResp.id;
            }
          } else {
            sumbitData.delete_cover_image = true;
          }

          if (listItemImages && listItemImages.length > 0) {
            const uploadedNewImage =
              listItemImages[0].file !== null && listItemImages[0].file !== undefined;

            if (uploadedNewImage) {
              const fileResp = await postFile({
                fieldPath: 'list_image',
                file: listItemImages[0].file,
                resource: 'Products',
              });

              sumbitData.new_list_image = fileResp.id;
            }
          } else {
            sumbitData.delete_list_image = true;
          }

          await editById(
            {
              id: sumbitData._id,
              resourceV2: 'AdminProduct',
              updateTypeV2: 'General',
              updateDataV2: isSynced ? {
                isSynced: true,
                hidden: submitedData.hidden,
                display_only_as_variation: submitedData.display_only_as_variation,
                manual_rkeeper_dish_code: submitedData.manual_rkeeper_dish_code,
                restaurants: submitedData.restaurants || [],
                hidden_in_restaurants: submitedData.hidden_in_restaurants || [],
                categories: submitedData.categories || [],
                categories_override: submitedData.categories_override || [],
                display_option: submitedData.display_option || '',
                name: submitedData.name,
                description: submitedData.description,
                is_new: submitedData.is_new,
                is_popular: submitedData.is_popular,
                is_vegetarian: submitedData.is_vegetarian,
                is_age_restricted: submitedData.is_age_restricted,
                is_recommended: submitedData.is_recommended,
                spicy: submitedData.spicy,
                is_long_delivery: submitedData.is_long_delivery || false,
                long_delivery_prepare: submitedData.long_delivery_prepare || 0,
                prepare_from: submitedData.prepare_from || 0,
                prepare_to: submitedData.prepare_to || 0,
                order: submitedData.order || 0,
                nutrition: submitedData.nutrition,
                cover_image: sumbitData.new_cover_image,
                delete_cover_image: sumbitData.delete_cover_image,
                list_image: sumbitData.new_list_image,
                delete_list_image: sumbitData.delete_list_image,
              } : {
                isSynced: false,
                hidden: submitedData.hidden,
                display_only_as_variation: submitedData.display_only_as_variation,
                restaurants: submitedData.restaurants || [],
                hidden_in_restaurants: submitedData.hidden_in_restaurants || [],
                categories: submitedData.categories || [],
                manual_rkeeper_dish_code: submitedData.manual_rkeeper_dish_code,
                categories_override: submitedData.categories_override || [],
                name: submitedData.name,
                description: submitedData.description,
                display_option: submitedData.display_option || '',
                is_new: submitedData.is_new,
                is_popular: submitedData.is_popular,
                is_vegetarian: submitedData.is_vegetarian,
                is_age_restricted: submitedData.is_age_restricted,
                is_recommended: submitedData.is_recommended,
                spicy: submitedData.spicy,
                is_long_delivery: submitedData.is_long_delivery || false,
                long_delivery_prepare: submitedData.long_delivery_prepare || 0,
                prepare_from: submitedData.prepare_from || 0,
                prepare_to: submitedData.prepare_to || 0,
                order: submitedData.order || 0,
                nutrition: submitedData.nutrition,
                cover_image: sumbitData.new_cover_image,
                delete_cover_image: sumbitData.delete_cover_image,
                list_image: sumbitData.new_list_image,
                delete_list_image: sumbitData.delete_list_image,
              },
              companyIdForV2: auth.company?.company,
            },
            async () => {
              navigate(urlRootName);
            },
            () => {
              setLoading(false);
            },
          );
        },
      );
    }
  };
  const onError = (data) => console.log(data);

  const getRestaurantsDataSource = () => {
    const allRestaurants = [{ _id: '-1', name: '-', address: '' }, ...restaurants];

    return allRestaurants
      ?.filter((r) => !selectedRestaurants.includes(r._id))
      .map((restaurant) => ({
        label: restaurant.name,
        value: restaurant._id,
      }));
  };

  const getHiddenRestaurantsDataSource = () => {
    const allRestaurants = [{ _id: '-1', name: '-', address: '' }, ...restaurants];

    return allRestaurants
      ?.filter((r) => !hiddenRestaurants.includes(r._id))
      .map((restaurant) => ({
        label: restaurant.name,
        value: restaurant._id,
      }));
  };

  const getSpicyDataSource = () => {
    return [
      { value: '0', label: 'Not defined' },
      { value: '1', label: '🌶' },
      { value: '2', label: '🌶🌶' },
      { value: '3', label: '🌶🌶🌶' },
    ];
  };

  const deleteSelectedRestaurant = (restaurantId) => {
    const newRestaurants = selectedRestaurants.filter((r) => r !== restaurantId);

    setSelectedRestaurants(newRestaurants);
    onRestaurantsChange(newRestaurants);
    setValue('restaurants', newRestaurants);
  };

  const deleteHiddenRestaurant = (restaurantId) => {
    const newRestaurants = hiddenRestaurants.filter((r) => r !== restaurantId);

    setHiddenRestaurants(newRestaurants);
    setValue('hidden_in_restaurants', newRestaurants);
  };

  const renderSelectedRestaurants = () => {
    if (!selectedRestaurants) return null;

    return (
      <div className="mt-2">
        {selectedRestaurants.map((restaurant) => {
          const restaurantData = restaurants?.find((r) => r._id === restaurant);

          if (!restaurantData) return null;
          return (
            <div key={restaurantData._id}>
              <SelectedItemButton
                name={restaurantData?.name}
                onClick={() => deleteSelectedRestaurant(restaurantData?._id)}
              />
            </div>
          );
        })}
      </div>
    );
  };

  const renderHiddenRestaurants = () => {
    if (!hiddenRestaurants) return null;

    return (
      <div className="mt-2">
        {hiddenRestaurants.map((restaurant) => {
          const restaurantData = restaurants?.find((r) => r._id === restaurant);

          if (!restaurantData) return null;
          return (
            <div key={restaurantData._id}>
              <SelectedItemButton
                name={restaurantData?.name}
                onClick={() => deleteHiddenRestaurant(restaurantData?._id)}
              />
            </div>
          );
        })}
      </div>
    );
  };

  const deleteSelectedProductCategory = (catId) => {
    const newProductCategories = selectedProductCategories.filter((pc) => pc !== catId);

    setSelectedProductCategories(newProductCategories);
    setValue('categories', newProductCategories);
  };

  const deleteSelectedProductCategoryOverride = (catId) => {
    const newProductCategories = selectedProductCategoriesOverride.filter((pc) => pc !== catId);

    setSelectedProductCategoriesOverride(newProductCategories);
    setValue('categories_override', newProductCategories);
  };

  const deleteSelectedVariationJoinOverride = (joinId) => {
    const newVariationJoins = selectedVariationJoinsOverride.filter((pc) => pc !== joinId);

    setSelectedVariationJoinsOverride(newVariationJoins);
  };

  const getProductCategoriesDataSource = () => {
    const categories = productCategories?.filter((c) => !selectedProductCategories.includes(c._id));

    const categoryOptions = enrichProductCategoriesResponse(categories)
      ?.sort(sortByRootParent)
      ?.map((pc) => ({
        label: pc.displayName,
        value: pc._id,
      }));

    categoryOptions?.unshift({
      label: '-',
      value: '-1',
    });

    return categoryOptions;
  };

  const getProductCategoriesOverrideDataSource = () => {
    const categories = productCategories?.filter(
      (c) => !selectedProductCategoriesOverride.includes(c._id),
    );

    const categoryOptions = enrichProductCategoriesResponse(categories)
      ?.sort(sortByRootParent)
      ?.map((pc) => ({
        label: pc.displayName,
        value: pc._id,
      }));

    categoryOptions?.unshift({
      label: '-',
      value: '-1',
    });

    return categoryOptions;
  };

  const getVariationJoinsOverrideDataSource = () => {
    const filteredVariationJoins = variationJoins?.filter(
      (c) => !selectedVariationJoinsOverride.includes(c._id),
    );

    const variationJoinsOptions = filteredVariationJoins?.map((pc) => ({
      label: pc.template_name,
      value: pc._id,
    }));

    variationJoinsOptions?.unshift({
      label: '-',
      value: '-1',
    });

    return variationJoinsOptions;
  };

  const renderSelectedCategories = () => {
    if (!selectedProductCategories) return null;

    return (
      <div className="mt-2">
        {selectedProductCategories.map((spc) => {
          const productCategory = enrichProductCategoriesResponse(productCategories)?.find(
            (pc) => pc._id === spc,
          );

          if (!productCategory) return null;

          // eslint-disable-next-line padding-line-between-statements
          return (
            <div key={`selected_pc_${productCategory?._id}`}>
              <SelectedItemButton
                name={getTranslatedData(productCategory?.name)}
                onClick={() => deleteSelectedProductCategory(productCategory?._id)}
              />
            </div>
          );
        })}
      </div>
    );
  };

  const renderSelectedCategoriesOverride = () => {
    if (!selectedProductCategoriesOverride) return null;

    return (
      <div className="mt-2">
        {selectedProductCategoriesOverride.map((spc) => {
          const productCategory = enrichProductCategoriesResponse(productCategories)?.find(
            (pc) => pc._id === spc,
          );

          if (!productCategory) return null;

          // eslint-disable-next-line padding-line-between-statements
          return (
            <div key={`selected_pc_${productCategory?._id}`}>
              <SelectedItemButton
                name={getTranslatedData(productCategory?.name)}
                onClick={() => deleteSelectedProductCategoryOverride(productCategory?._id)}
              />
            </div>
          );
        })}
      </div>
    );
  };

  const renderSelectedVariationJoinsOverride = () => {
    if (!selectedVariationJoinsOverride) return null;

    return (
      <div className="mt-2">
        {selectedVariationJoinsOverride.map((spc) => {
          const variationJoin = variationJoins?.find((pc) => pc._id === spc);

          if (!variationJoin) return null;

          // eslint-disable-next-line padding-line-between-statements
          return (
            <div key={`selected_join_${variationJoin?._id}`}>
              <SelectedItemButton
                name={variationJoin?.template_name}
                onClick={() => deleteSelectedVariationJoinOverride(variationJoin?._id)}
              />
            </div>
          );
        })}
      </div>
    );
  };

  const renderVariationTypeName = (variationType) => {
    return Config.DATA.VARIATION_TYPES.find((vt) => vt.value === variationType)?.label || 'Unknown';
  };

  const onEditClicked = (item) => {
    setSelectedVariation(item);
    setModalIsVisible(true);
  };

  const onTagEditClicked = (item, ind) => {
    setSelectedTag(item);
    setSelectedTagIndex(ind);
    setTagModalIsVisible(true);
  };

  const onDeleteClicked = async (item) => {
    await deleteById(
      {
        collectionName: Config.DATA.COLLECTIONS.PRODUCT_VARIATIONS,
        token: auth.token,
        id: item._id,
        resourceV2: 'AdminProductVariation',
        companyIdForV2: auth.company?.company,
      },
      async () => {
        getProductVariations(getVariationsRequestData, true);
      },
    );
  };

  const onDeleteTag = async (item) => {
    const updatedTags = (customTags || []).filter((x) => x !== item);
    const updateClause = { $set: { tags: updatedTags } };

    await editById(
      {
        collectionName: Config.DATA.COLLECTIONS.PRODUCTS,
        id: itemData._id,
        updateClause,
        token: auth.token,
        resourceV2: 'AdminProduct',
        updateTypeV2: 'Tags',
        updateDataV2: updateClause.$set,
        companyIdForV2: auth.company?.company,
      },
      () => {
        setCustomTags(updatedTags);
      },
    );
  };

  const onCoverImageChange = (imageList) => {
    // data for submit
    setCoverImages(imageList);
  };

  const onListImageChange = (imageList) => {
    // data for submit
    setListItemImages(imageList);
  };

  return (
    <>
      {modalIsVisible && (
        <ProductVariationTemplatesAddEditForm
          productId={itemData?._id}
          itemData={selectedVariation}
          isOpen={modalIsVisible}
          closeModal={() => setModalIsVisible(false)}
          onSaved={() => {
            setModalIsVisible(false);
            setSelectedVariation(null);
            getProductVariations(getVariationsRequestData, true);
          }}
          isSynced={isSynced}
          allProducts={allProducts}
        />
      )}
      {tagModalIsVisible && (
        <ProductCustomTagAddEditForm
          closeModal={() => setTagModalIsVisible(false)}
          isOpen={tagModalIsVisible}
          itemData={selectedTag}
          onSaved={(newTags) => {
            setCustomTags(newTags);
            setTagModalIsVisible(false);
            setSelectedTag(null);
            setSelectedTagIndex(-1);
          }}
          productId={itemData?._id}
          allTags={customTags}
          tagIndex={selectedTagIndex}
        />
      )}
      <form onSubmit={handleSubmit(onSubmit, onError)} action="#" method="POST">
        <div className="shadow sm:rounded-md sm:overflow-hidden">
          <div className="bg-white py-6 px-4 space-y-6 sm:p-6">
            <div>
              <h3 className="text-lg leading-6 font-medium text-gray-900">General</h3>
            </div>

            <div className="grid grid-cols-6 gap-6">
              <div className="col-span-6 sm:col-span-3">
                <CheckboxWrapper
                  register={register}
                  name="hidden"
                  title={
                    itemData && itemData.unhide_disabled
                      ? 'Hidden (product no longer synced)'
                      : 'Hidden'
                  }
                  subtitle="hide product from app completely"
                  disabled={itemData && itemData.unhide_disabled}
                  isDanger={itemData && itemData.unhide_disabled}
                />

                <CheckboxWrapper
                  register={register}
                  name="display_only_as_variation"
                  title="Display only as variation"
                  subtitle="only displays as another product's variation"
                  responsiveClassName="mt-3"
                />
              </div>

              <DropdownWrapper
                name="hidden_in_restaurants"
                title="Hidden in Restaurants"
                subtitle="can select only specific restaurants to hide in"
                options={getHiddenRestaurantsDataSource()}
                error={errors?.hidden_in_restaurants}
                responsiveClassName="col-span-6 sm:col-span-3"
                onChange={(e) => {
                  setHiddenRestaurants((oldArray) => [...oldArray, e.target.value]);
                  setValue('hidden_in_restaurants', [...hiddenRestaurants, e.target.value]);
                }}
                renderSelections={renderHiddenRestaurants}
              />

              <div className="col-span-6 w-full h-[1px] bg-gray-100 mt-2 mb-2" />

              <div className="col-span-6 sm:col-span-3">
                <DropDownList
                  showLabel
                  id="restaurants"
                  name="restaurants"
                  label="Restaurants"
                  options={getRestaurantsDataSource()}
                  className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md"
                  onChange={(e) => {
                    setSelectedRestaurants((oldArray) => {
                      const newArray = [...oldArray, e.target.value];

                      onRestaurantsChange(newArray);
                      return newArray;
                    });
                    setValue('restaurants', [...selectedRestaurants, e.target.value]);
                  }}
                />
                {errors?.restaurants && (
                  <p className="mt-2 text-sm text-red-600" id="email-error">
                    {errors?.restaurants.message}
                  </p>
                )}
                {renderSelectedRestaurants()}
              </div>

              <div className="col-span-6 sm:col-span-3 ">
                <DropDownList
                  showLabel
                  id="categories"
                  name="categories*"
                  label="Categories"
                  options={getProductCategoriesDataSource()}
                  className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md"
                  onChange={(e) => {
                    setSelectedProductCategories((oldArray) => [...oldArray, e.target.value]);
                    setValue('categories', [...selectedProductCategories, e.target.value]);
                  }}
                />
                {errors?.categories && (
                  <p className="mt-2 text-sm text-red-600" id="email-error">
                    {errors?.categories.message}
                  </p>
                )}
                {renderSelectedCategories()}
              </div>

              {isSynced && (
                <>
                  <div className="col-span-6 sm:col-span-3 ">
                    <DropDownList
                      showLabel
                      id="categories_override"
                      name="categories_override*"
                      label="Categories override"
                      subtitle="Override categories set in external providers"
                      options={getProductCategoriesOverrideDataSource()}
                      className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md"
                      onChange={(e) => {
                        setSelectedProductCategoriesOverride((oldArray) => [
                          ...oldArray,
                          e.target.value,
                        ]);
                        setValue('categories_override', [
                          ...selectedProductCategories,
                          e.target.value,
                        ]);
                      }}
                    />
                    {errors?.categories_override && (
                      <p className="mt-2 text-sm text-red-600" id="email-error">
                        {errors?.categories_override.message}
                      </p>
                    )}
                    {renderSelectedCategoriesOverride()}
                  </div>

                  <div className="sm:col-span-3 hidden sm:block" />
                </>
              )}

              <div className="col-span-6 w-full h-[1px] bg-gray-100 mt-2 mb-2" />

              <div className="sm:col-span-3 col-span-6">
                <label
                  htmlFor="listItemPhoto"
                  className="block text-sm font-medium text-gray-700 mb-1"
                >
                  List item photo
                </label>
                <ImageUpload
                  id="listItemPhoto"
                  name="listItemPhoto"
                  images={listItemImages}
                  onChange={onListImageChange}
                />
              </div>
              <div className="sm:col-span-3 col-span-6">
                <label
                  htmlFor="coverImage"
                  className="block text-sm font-medium text-gray-700 mb-1"
                >
                  Cover photo
                </label>
                <ImageUpload
                  id="coverImage"
                  name="coverImage"
                  images={coverImages}
                  onChange={onCoverImageChange}
                />
              </div>

              <div className="sm:col-span-3 col-span-6">
                <TranslatableInput
                  name="name"
                  register={register}
                  error={errors?.name}
                  containerClassName="sm:col-span-3"
                  labelClassName="block text-sm font-medium text-gray-700"
                  className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md"
                  placeholder="Product name*"
                />
              </div>
              <div className="sm:col-span-3 hidden sm:block" />
              <div className="sm:col-span-3 col-span-6">
                <TranslatableInput
                  name="description"
                  register={register}
                  error={errors?.description}
                  containerClassName="sm:col-span-3"
                  labelClassName="block text-sm font-medium text-gray-700"
                  className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md"
                  placeholder="Enter description"
                  renderAsTextArea
                />
              </div>
              <div className="sm:col-span-3 col-span-6">
                <TranslatableInput
                  name="nutrition"
                  register={register}
                  error={errors?.nutrition}
                  containerClassName="sm:col-span-3"
                  labelClassName="block text-sm font-medium text-gray-700"
                  className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md"
                  placeholder="Enter nutrition info"
                  renderAsTextArea
                />
              </div>

              <div className="col-span-6 w-full h-[1px] bg-gray-100 mt-2 mb-2" />

              <div className="lg:col-span-4 col-span-6">
                <fieldset>
                  <div>
                    <legend className="text-base font-medium text-gray-900">Product options</legend>
                  </div>
                  <div className="mt-4 space-y-4">
                    <div className="flex items-center">
                      <Input
                        {...register('is_new')}
                        id="is_new"
                        name="is_new"
                        type="checkbox"
                        className="focus:ring-indigo-500 h-4 w-4 text-indigo-600 border-gray-300"
                      />
                      <label
                        htmlFor="is_new"
                        className="ml-3 block text-sm font-medium text-gray-700"
                      >
                        New
                      </label>
                    </div>
                    <div className="flex items-center">
                      <Input
                        {...register('is_popular')}
                        name="is_popular"
                        id="is_popular"
                        type="checkbox"
                        className="focus:ring-indigo-500 h-4 w-4 text-indigo-600 border-gray-300"
                      />
                      <label
                        htmlFor="is_popular"
                        className="ml-3 block text-sm font-medium text-gray-700"
                      >
                        Popular
                      </label>
                    </div>
                    <div className="flex items-center">
                      <Input
                        {...register('is_vegetarian')}
                        name="is_vegetarian"
                        id="is_vegetarian"
                        type="checkbox"
                        className="focus:ring-indigo-500 h-4 w-4 text-indigo-600 border-gray-300"
                      />
                      <label
                        htmlFor="is_vegetarian"
                        className="ml-3 block text-sm font-medium text-gray-700"
                      >
                        Vegetarian
                      </label>
                    </div>
                    <div className="flex items-center">
                      <Input
                        {...register('is_age_restricted')}
                        name="is_age_restricted"
                        id="is_age_restricted"
                        type="checkbox"
                        className="focus:ring-indigo-500 h-4 w-4 text-indigo-600 border-gray-300"
                      />
                      <label
                        htmlFor="is_age_restricted"
                        className="ml-3 block text-sm font-medium text-gray-700"
                      >
                        Age restricted
                      </label>
                    </div>
                    <div className="flex items-center">
                      <Input
                        {...register('is_recommended')}
                        name="is_recommended"
                        id="is_recommended"
                        type="checkbox"
                        className="focus:ring-indigo-500 h-4 w-4 text-indigo-600 border-gray-300"
                      />
                      <label
                        htmlFor="is_recommended"
                        className="ml-3 block text-sm font-medium text-gray-700"
                      >
                        Recommended
                      </label>
                    </div>
                    <div className="flex items-center mt-4">
                      <DropDownList
                        showLabel
                        {...register('spicy')}
                        id="spicy"
                        name="spicy"
                        label="Is it spicy?"
                        options={getSpicyDataSource()}
                        className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md"
                      />
                    </div>
                  </div>
                </fieldset>
              </div>
              <div className="lg:col-span-2 hidden lg:block" />

              <CheckboxWrapper
                register={register}
                name="is_long_delivery"
                title="Long delivery"
                subtitle="if delivery takes days"
                responsiveClassName="col-span-6"
                onChange={(e) => {
                  setIsLongDelivery(e.target.checked);
                }}
              />

              {isLongDeliver && (
                <>
                  <TextboxWrapper
                    register={register}
                    name="long_delivery_prepare"
                    title="Days to prepare"
                    subtitle="Amount of days it will take to prepare"
                    responsiveClassName="sm:col-span-3 col-span-6"
                    error={errors?.long_delivery_prepare}
                  />
                  <div className="sm:col-span-3 sm:block hidden" />
                </>
              )}

              {!isLongDeliver && (
                <div className="sm:col-span-3 col-span-6">
                  <label htmlFor="prepare_from" className="block text-sm font-medium text-gray-700">
                    Delivery time from
                  </label>
                  <div className="mt-1  relative rounded-md shadow-sm">
                    <Input
                      {...register('prepare_from')}
                      className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md"
                      id="prepare_from"
                      type="text"
                      errorMessage={errors?.prepare_from}
                    />
                  </div>
                  <p className="mt-2 text-sm text-gray-500" id="email-description">
                    Average delivery time <strong>from</strong> in minutes
                  </p>
                  {errors?.prepare_from && (
                    <p className="mt-2 text-sm text-red-600" id="email-error">
                      {errors?.prepare_from.message}
                    </p>
                  )}
                </div>
              )}
              {!isLongDeliver && (
                <div className="sm:col-span-3 col-span-6">
                  <label htmlFor="prepare_to" className="block text-sm font-medium text-gray-700">
                    Delivery time to
                  </label>
                  <div className="mt-1  relative rounded-md shadow-sm">
                    <Input
                      {...register('prepare_to')}
                      className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md"
                      id="prepare_to"
                      type="text"
                      errorMessage={errors?.prepare_to}
                    />
                  </div>
                  <p className="mt-2 text-sm text-gray-500" id="email-description">
                    Average delivery time <strong>to</strong> in minutes
                  </p>
                  {errors?.prepare_to && (
                    <p className="mt-2 text-sm text-red-600" id="email-error">
                      {errors?.prepare_to.message}
                    </p>
                  )}
                </div>
              )}

              <div className="sm:col-span-3 col-span-6">
                <label htmlFor="order" className="block text-sm font-medium text-gray-700">
                  Display order
                </label>
                <div className="mt-1  relative rounded-md shadow-sm">
                  <Input
                    {...register('order')}
                    className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md"
                    id="order"
                    type="text"
                    errorMessage={errors?.order}
                  />
                </div>
                {errors?.order && (
                  <p className="mt-2 text-sm text-red-600" id="email-error">
                    {errors?.order.message}
                  </p>
                )}
              </div>

              <DropdownWrapper
                register={register}
                name="display_option"
                title="Display options"
                options={displayTypeOption}
                error={errors?.display_option}
                responsiveClassName="col-span-6 sm:col-span-3"
              />

              <div className="col-span-6 w-full h-[1px] bg-gray-100 mt-2 mb-2" />

              <div className="col-span-6">
                <fieldset>
                  <div className="mb-6">
                    <legend className="text-base font-medium text-gray-900">
                      External product (optional)
                    </legend>
                    <p className="text-sm text-gray-500 mt-1">
                      Product identifier code from integration
                    </p>
                  </div>
                  <div className="grid grid-cols-6 gap-6">
                    {!isSynced && (
                      <TextboxWrapper
                        register={register}
                        name="manual_rkeeper_dish_code"
                        title="External ID"
                        responsiveClassName="col-span-6 sm:col-span-3"
                        error={errors?.manual_rkeeper_dish_code}
                      />
                    )}
                    {isSynced && (
                      <TextboxWrapper
                        value={itemData?.rkeeper_dish_code}
                        name="rkeeper_dish_code"
                        title="External ID"
                        responsiveClassName="col-span-6 sm:col-span-3"
                        disabled
                      />
                    )}
                  </div>
                </fieldset>
              </div>
            </div>
          </div>
          <div className="px-4 py-3 bg-gray-50 text-right sm:px-6">
            <button
              type="button"
              className="bg-white py-2 px-4 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
              onClick={() => navigate(urlRootName)}
              disabled={loading}
            >
              Cancel
            </button>
            <FormButton text="Save" isLoading={loading} />
          </div>
        </div>
      </form>

      <form action="#" method="POST">
        {isEdit && (
          <div className="bg-white shadow sm:rounded-md overflow-hidden mt-10">
            <div className="pt-6 px-4 space-y-6 sm:px-6">
              <div>
                <h3 className="text-lg leading-6 font-medium text-gray-900">Variations</h3>
                <p className="mt-1 text-sm text-gray-500">Your product variations</p>
              </div>
            </div>

            {productVariations && productVariations.length > 0 && (
              <div className="px-4 sm:px-6 mb-6">
                <List source={productVariations} onRowClick={onEditClicked}>
                  <DataColumn
                    name="Variation name"
                    dbColumnName="name"
                    headerClass="px-3 py-3.5 text-left text-sm font-semibold text-gray-900"
                    dataCellClass="whitespace-nowrap px-3 py-4 text-sm text-gray-500"
                    render={(item) => getTranslatedData(item.name)}
                    stackedColumns={[
                      {
                        name: 'Type',
                        render: (item) => renderVariationTypeName(item.variation_type),
                        class: 'mt-1 truncate text-gray-400 sm:hidden',
                      },
                    ]}
                  />
                  <DataColumn
                    name="Type"
                    dbColumnName="variation_type"
                    render={(item) => renderVariationTypeName(item.variation_type)}
                    headerClass="hidden px-3 py-3.5 text-left text-sm font-semibold text-gray-900 sm:table-cell"
                    dataCellClass="hidden whitespace-nowrap px-3 py-4 text-sm text-gray-500 sm:table-cell"
                  />
                  <DataColumn
                    name="Order"
                    dbColumnName="order"
                    headerClass="hidden px-3 py-3.5 text-left text-sm font-semibold text-gray-900 sm:table-cell"
                    dataCellClass="hidden whitespace-nowrap px-3 py-4 text-sm text-gray-500 sm:table-cell"
                  />
                  <DataColumn
                    name="Hidden dine-in"
                    dbColumnName="hidden_dinein"
                    render={(item) => {
                      return item.hidden || item.hidden_dinein ? (
                        <CheckCircleIcon className="h-5 w-5" aria-hidden="true" />
                      ) : null;
                    }}
                    headerClass="hidden px-3 py-3.5 text-left text-sm font-semibold text-gray-900 sm:table-cell"
                    dataCellClass="hidden whitespace-nowrap px-3 py-4 text-sm text-gray-500 sm:table-cell"
                  />
                  <DataColumn
                    name="Hidden delivery"
                    dbColumnName="hidden_delivery"
                    render={(item) => {
                      return item.hidden || item.hidden_delivery ? (
                        <CheckCircleIcon className="h-5 w-5" aria-hidden="true" />
                      ) : null;
                    }}
                    headerClass="hidden px-3 py-3.5 text-left text-sm font-semibold text-gray-900 sm:table-cell"
                    dataCellClass="hidden whitespace-nowrap px-3 py-4 text-sm text-gray-500 sm:table-cell"
                  />
                  {!isSynced && (
                    <LinkColumn
                      title="Delete"
                      onClick={onDeleteClicked}
                      askForConfirmation
                      className="relative py-3.5 pl-3 sm:pr-6"
                      dataCellClass="whitespace-nowrap py-4 pl-3 pr-4 text-right text-sm font-medium sm:pr-6"
                    />
                  )}
                </List>
              </div>
            )}
            {(!productVariations || productVariations.length === 0) && <div className="mb-6" />}

            {!isSynced && (
              <div className="px-4 py-3 bg-gray-50 text-right sm:px-6">
                <button
                  type="button"
                  onClick={() => {
                    setSelectedVariation(null);
                    setModalIsVisible(true);
                  }}
                  disabled={isNew}
                  className={joinClassNames(
                    isNew ? 'disabled:opacity-50' : '',
                    'bg-indigo-600 border border-transparent rounded-md shadow-sm py-2 px-4 inline-flex justify-center text-sm font-medium text-white hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500',
                  )}
                >
                  Add new
                </button>
              </div>
            )}

            {isSynced && (
              <div className="px-4 sm:px-6 pt-3 pb-6">
                <div className="col-span-6 w-full h-[1px] bg-gray-100 mt-2 mb-4" />
                <div className="col-span-6 sm:col-span-3 ">
                  <DropDownList
                    showLabel
                    id="variation_joins"
                    name="variation_joins*"
                    label="Variation joins"
                    subtitle="Override displayed variations with variation joins. Joins will group your product variations in a custom way."
                    options={getVariationJoinsOverrideDataSource()}
                    className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md"
                    onChange={(e) => {
                      setSelectedVariationJoinsOverride((oldArray) => [
                        ...oldArray,
                        e.target.value,
                      ]);
                    }}
                  />
                  {renderSelectedVariationJoinsOverride()}
                </div>

                <button
                  type="button"
                  onClick={() => {
                    saveProductVariationJoins();
                  }}
                  className={joinClassNames(
                    'mt-3 bg-indigo-600 border border-transparent rounded-md shadow-sm py-2 px-4 inline-flex justify-center text-sm font-medium text-white hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500',
                  )}
                  disabled={loadingVariationJoins}
                >
                  Save joins
                </button>
              </div>
            )}
          </div>
        )}
      </form>

      <form action="#" method="POST">
        {isEdit && (
          <div className="bg-white shadow sm:rounded-md overflow-hidden mt-10">
            <div className="pt-6 px-4 space-y-6 sm:px-6">
              <div>
                <h3 className="text-lg leading-6 font-medium text-gray-900">Custom tags</h3>
                <p className="mt-1 text-sm text-gray-500">Your product custom tags</p>
              </div>
            </div>

            {customTags && customTags.length > 0 && (
              <div className="px-4 sm:px-6 mb-6">
                <List keyField="_index" source={customTags} onRowClick={onTagEditClicked}>
                  <DataColumn
                    name="Tag"
                    dbColumnName="tag"
                    headerClass="px-3 py-3.5 text-left text-sm font-semibold text-gray-900"
                    dataCellClass="whitespace-nowrap px-3 py-4 text-sm text-gray-500"
                    render={(item) => getTranslatedData(item.tag)}
                  />
                  <LinkColumn
                    title="Delete"
                    onClick={onDeleteTag}
                    askForConfirmation
                    className="relative py-3.5 pl-3 sm:pr-6"
                    dataCellClass="whitespace-nowrap py-4 pl-3 pr-4 text-right text-sm font-medium sm:pr-6"
                  />
                </List>
              </div>
            )}
            {(!customTags || customTags.length === 0) && <div className="mb-6" />}

            <div className="px-4 py-3 bg-gray-50 text-right sm:px-6">
              <button
                type="button"
                onClick={() => {
                  setSelectedTag(null);
                  setTagModalIsVisible(true);
                }}
                disabled={isNew}
                className={joinClassNames(
                  isNew ? 'disabled:opacity-50' : '',
                  'bg-indigo-600 border border-transparent rounded-md shadow-sm py-2 px-4 inline-flex justify-center text-sm font-medium text-white hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500',
                )}
              >
                Add new
              </button>
            </div>
          </div>
        )}
      </form>
    </>
  );
};
